import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { View } from 'react-native';
import Toast from 'react-native-root-toast';
import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { Alert } from '../../components/other/Alert';

import { useEventCallback } from '../../../common/utils/hooks';
import { GET_SERVICE_DOCKET_FOR_CONFIRM_STOCK_LEVELS } from '../../../common/graphql/serviceDocket';

import { Screen } from '../../design/components';
import { useFreshDetailQueryOptions } from '../../hooks';
import useBackLabel from '../../hooks/useBackLabel';
import useFramedDetailStates from '../../hooks/useFramedDetailStates';
import { useOfflineQueue } from '../../offline';

import ConfirmStockLevels from './ConfirmStockLevels';
import TrainingModeBanner from '../myDay/TrainingModeBanner';
import { isMyDayTraining } from '../myDay/myDayTraining';

const ConfirmStockLevelsView = () => {
	const navigation = useNavigation();
	const route = useRoute();
	const { isOnline } = useOfflineQueue();

	const docketId = route.params?.docketId;
	const trainingContext = route.params?.myDayTraining;
	const isTraining = isMyDayTraining(trainingContext);
	const restockCorrectionStockReviewRevision = route.params?.restockCorrectionStockReviewRevision;
	const {
		canRenderDetailQuery,
		detailQueryOptions,
		blockDataWhileLoading,
	} = useFreshDetailQueryOptions({ noCache: isTraining });

	const [anyUnsavedChanges, setUnsavedChanges] = useState(false);
	const unsavedChangesRef = useRef(false);
	// Keep the requested destination through the expiry-date confirmation dialog.
	const pendingExitActionRef = useRef(null);
	const warningOpenRef = useRef(false);
	const [confirmStockLevels, setConfirmStockLevels] = useState(null);

	const setAnyUnsavedChanges = useCallback(value => {
		// Confirmation clears this immediately before navigating, ahead of React's next render.
		unsavedChangesRef.current = value;
		setUnsavedChanges(value);
	}, []);
	const onCancelConfirmation = useCallback(() => {
		pendingExitActionRef.current = null;
	}, []);
	const onConfirmed = useEventCallback(() => {
		const action = pendingExitActionRef.current;
		pendingExitActionRef.current = null;
		if (action) {
			navigation.dispatch(action);
		}
		else {
			navigation.goBack();
		}
	});

	const onBeforeRemove = useEventCallback(event => {
		if (!unsavedChangesRef.current) {
			return;
		}
		event.preventDefault();
		if (warningOpenRef.current) {
			return;
		}
		warningOpenRef.current = true;
		openWarning(
			() => navigation.dispatch(event.data.action),
			async () => {
				pendingExitActionRef.current = event.data.action;
				try {
					const result = await confirmStockLevels();
					if (result?.confirmationAborted) {
						onCancelConfirmation();
					}
					return result;
				}
				catch (error) {
					onCancelConfirmation();
					throw error;
				}
			},
			onConfirmed,
			() => { warningOpenRef.current = false; },
			isTraining && !isOnline,
		);
	});
	useEffect(() => navigation.addListener('beforeRemove', onBeforeRemove), [navigation, onBeforeRemove]);

	const backLabel = useBackLabel();
	const banner = useMemo(() => (
		<TrainingModeBanner trainingContext={trainingContext} isOnline={isOnline} />
	), [isOnline, trainingContext]);
	const framedStates = useFramedDetailStates({ headerRight: <ServiceNoteButton />, backLabel, banner });

	return (
		<View style={{ flex: 1 }}>
			{canRenderDetailQuery ? (
				<ConfirmStockLevels
					id={docketId}
					query={GET_SERVICE_DOCKET_FOR_CONFIRM_STOCK_LEVELS}
					setAnyUnsavedChanges={setAnyUnsavedChanges}
					setConfirmStockLevels={setConfirmStockLevels}
					anyUnsavedChanges={anyUnsavedChanges}
					detailQueryOptions={detailQueryOptions}
					blockDataWhileLoading={blockDataWhileLoading}
					restockCorrectionStockReviewRevision={restockCorrectionStockReviewRevision}
					trainingContext={trainingContext}
					backLabel={backLabel}
					onConfirmed={onConfirmed}
					onCancelConfirmation={onCancelConfirmation}
					banner={banner}
					nextStepLabel={route.params?.nextStepLabel}
					{...framedStates}
				/>
			) : (
				<Screen headerRight={<ServiceNoteButton />} backLabel={backLabel} banner={banner} isLoading={true} />
			)}
		</View>
	);
};

function openWarning (onDiscard, confirmStockLevels, onConfirmed, onClose, disableConfirm = false) {
	Alert.alert(
		'Warning',
		disableConfirm
			? 'Going back will discard any changes you have made to stock levels. Reconnect before confirming training changes.'
			: 'Going back will discard any changes you have made to stock levels. To save your current stock levels and go back, select "Confirm and go back".',
		[
			...(!disableConfirm ? [{
				text: 'Confirm and go back',
				onPress: async () => {
					try {
						let showExpiryDatesDialogWarning = false;
						let confirmationAborted = false;
						if (confirmStockLevels) {
							const expiryDatesObj = await confirmStockLevels();
							({ showExpiryDatesDialogWarning, confirmationAborted } = expiryDatesObj || {});
						}
						if (!showExpiryDatesDialogWarning && !confirmationAborted) {
							onConfirmed();
						}
					}
					catch (error) {
						Toast.show(error?.message || 'Error when confirming stock levels');
					}
					finally {
						onClose();
					}
				},
			}] : []),
			{
				text: 'Discard',
				onPress: () => {
					onClose();
					onDiscard();
				},
			},
			{ text: 'Cancel', onPress: onClose, style: 'cancel' },
		],
		{ cancelable: false },
	);
}

export default ConfirmStockLevelsView;
